/**
 * Login — corporate sign-in, built to the handoff `LoginScreen`
 * (`design_handoff_as_life_portal/source/screen-misc.jsx`).
 *
 * This screen is fixed **navy chrome**: like the sidebar it is intentionally
 * NOT accent-driven, so the colours below are literal hexes (mirroring the
 * handoff) rather than `$primary` / accent tokens — the brand lockup must read
 * navy regardless of the selected accent.
 *
 * Two auth flows, selected by EXPO_PUBLIC_AUTH_FLOW (see src/auth/config → authFlow):
 *  - 'password' (default): **seamless in-app sign-in** (OIDC Direct Access Grant / ROPC) —
 *    username + password entered here are exchanged for tokens directly at Keycloak's token
 *    endpoint via `useAuth().loginWithPassword()`. Requires "Direct access grants" on the
 *    Keycloak client `as-life-web`.
 *  - 'pkce': browser redirect (Authorization Code + PKCE) via `useAuth().login()` — the OIDC
 *    target. Requires Standard Flow + redirect URI `aslife://auth/callback` on the client.
 * In dev-fallback it resolves immediately. `AuthGate` redirects off `/login` once authenticated.
 */
import { useRouter } from 'expo-router'
import { useState } from 'react'
import { Image, Linking, Platform } from 'react-native'
import { Input, Spinner, Stack, Text, XStack, YStack } from 'tamagui'
import Svg, { Defs, LinearGradient, RadialGradient, Rect, Stop } from 'react-native-svg'

import { useAuth } from '../src/auth/useAuth'
import { authFlow } from '../src/auth/config'
import { useApp } from '../src/i18n'
import { useResponsive } from '../src/lib/responsive'
import { Icon, IconButton, LanguageMenu } from '../src/ui'

const asMarkGold = require('../assets/brand/as-mark-gold.png')

// Fixed navy login chrome (not theme/accent driven) — mirrors the handoff.
const LOGIN = {
  bgBase: '#0F1C45',
  gradTop: '#0C1838',
  gradBottom: '#13245A',
  bloomTop: '#243A76',
  bloomBottom: '#1A2A5E',
  cardNavy: '#16265C', // h1
  subText: '#7A8398', // card subtitle
  labelText: '#3A4256', // field labels
  fieldBorder: '#E0E5EF',
  fieldText: '#16265C',
  placeholder: '#9AA3B6',
  button: '#21408F',
  buttonHover: '#1A3576',
  sso: '#9AA3B6',
  brandSub: '#A9B8DA',
  footer: '#8493BB',
  footerLink: '#CDAC74',
  iconMuted: '#8A93A8',
  error: '#C0584A',
} as const

// Brushed-bronze metallic stops (handoff `BRONZE`).
const BRONZE: Array<[number, string]> = [
  [0, '#9C7C47'],
  [0.18, '#D8BD85'],
  [0.36, '#B8945A'],
  [0.52, '#ECD6A2'],
  [0.68, '#B8945A'],
  [0.84, '#D8BD85'],
  [1, '#9C7C47'],
]

/** Brushed-bronze bar — horizontal metallic gradient + a soft top/bottom sheen. */
function BronzeBar({ w, h }: { w: number; h: number }) {
  return (
    <Svg width={w} height={h}>
      <Defs>
        <LinearGradient id="bz" x1="0%" y1="0%" x2="100%" y2="0%">
          {BRONZE.map(([off, c]) => (
            <Stop key={off} offset={off} stopColor={c} />
          ))}
        </LinearGradient>
        <LinearGradient id="bzSheen" x1="0%" y1="0%" x2="0%" y2="100%">
          <Stop offset={0} stopColor="#FFFFFF" stopOpacity={0.14} />
          <Stop offset={1} stopColor="#000000" stopOpacity={0.14} />
        </LinearGradient>
      </Defs>
      <Rect x={0} y={0} width={w} height={h} fill="url(#bz)" />
      <Rect x={0} y={0} width={w} height={h} fill="url(#bzSheen)" />
    </Svg>
  )
}

/** Full-bleed navy backdrop: diagonal base gradient + two soft radial blooms. */
function GradientBackdrop({ width, height }: { width: number; height: number }) {
  const far = Math.max(width, height)
  return (
    <Stack position="absolute" top={0} left={0} right={0} bottom={0}>
      <Svg width={width} height={height}>
        <Defs>
          <LinearGradient id="bg" x1={0} y1={0} x2={width * 0.34} y2={height} gradientUnits="userSpaceOnUse">
            <Stop offset={0} stopColor={LOGIN.gradTop} />
            <Stop offset={1} stopColor={LOGIN.gradBottom} />
          </LinearGradient>
          <RadialGradient id="b1" cx={width * 0.72} cy={height * -0.12} r={far * 0.6} gradientUnits="userSpaceOnUse">
            <Stop offset={0} stopColor={LOGIN.bloomTop} stopOpacity={0.85} />
            <Stop offset={0.6} stopColor={LOGIN.bloomTop} stopOpacity={0} />
          </RadialGradient>
          <RadialGradient id="b2" cx={width * -0.05} cy={height * 1.12} r={far * 0.5} gradientUnits="userSpaceOnUse">
            <Stop offset={0} stopColor={LOGIN.bloomBottom} stopOpacity={0.8} />
            <Stop offset={0.6} stopColor={LOGIN.bloomBottom} stopOpacity={0} />
          </RadialGradient>
        </Defs>
        <Rect x={0} y={0} width={width} height={height} fill="url(#bg)" />
        <Rect x={0} y={0} width={width} height={height} fill="url(#b1)" />
        <Rect x={0} y={0} width={width} height={height} fill="url(#b2)" />
      </Svg>
    </Stack>
  )
}

export default function LoginScreen() {
  const { l } = useApp()
  const router = useRouter()
  const { login, loginWithPassword, isAuthenticating, error, mustSetPassword } = useAuth()
  const { width, height } = useResponsive()

  const usePkce = authFlow === 'pkce'
  const [email, setEmail] = useState('')
  const [pw, setPw] = useState('')
  const [showPwd, setShowPwd] = useState(false)

  const cardW = Math.min(width - 48, 428)
  const submit = () => {
    if (isAuthenticating) return
    // PKCE: browser SSO redirect — no in-app credentials.
    // mustSetPassword: временный пароль принят, но сменить его можно только на странице
    // Keycloak — уходим в браузерный флоу. Именно по нажатию: popup без жеста блокируется.
    if (usePkce || mustSetPassword) {
      void login()
      return
    }
    if (!email.trim() || !pw) return
    void loginWithPassword(email.trim(), pw)
  }

  // Shared input style (handoff `field`): 48-tall, soft border, navy focus ring.
  const fieldProps = {
    height: 48,
    borderWidth: 1.5,
    borderColor: LOGIN.fieldBorder,
    borderRadius: 11,
    paddingHorizontal: 14,
    fontSize: 15,
    color: LOGIN.fieldText,
    backgroundColor: '#FFFFFF',
    placeholderTextColor: LOGIN.placeholder,
    focusStyle: {
      borderColor: LOGIN.button,
      outlineColor: 'rgba(33,64,143,0.18)',
      outlineWidth: 3,
      outlineStyle: 'solid',
    },
  } as const

  return (
    <YStack flex={1} backgroundColor={LOGIN.bgBase} alignItems="center" justifyContent="center" padding={24}>
      <GradientBackdrop width={width} height={height} />

      {/* brushed-bronze top edge */}
      <Stack position="absolute" top={0} left={0} right={0} height={6} zIndex={2}>
        <BronzeBar w={width} h={6} />
      </Stack>

      {/* language switcher — shared <LanguageMenu/> (shows the CURRENT language,
          same as the header) in fixed navy chrome */}
      <Stack position="absolute" top={22} right={24} zIndex={3}>
        <LanguageMenu variant="navy" />
      </Stack>

      <YStack width="100%" maxWidth={428} zIndex={1}>
        {/* brand lockup */}
        <YStack alignItems="center" gap={16} marginBottom={28}>
          <Image source={asMarkGold} style={{ width: 60, height: 56, resizeMode: 'contain' }} />
          <YStack alignItems="center">
            <Text color="#FFFFFF" fontSize={25} fontWeight="800" letterSpacing={6} paddingLeft={6}>
              AS LIFE
            </Text>
            <Text color={LOGIN.brandSub} fontSize={13} letterSpacing={0.3} marginTop={9}>
              {l('login.brandsub')}
            </Text>
          </YStack>
        </YStack>

        {/* card */}
        <YStack
          backgroundColor="#FFFFFF"
          borderRadius={18}
          overflow="hidden"
          shadowColor="#000000"
          shadowOpacity={0.45}
          shadowRadius={45}
          shadowOffset={{ width: 0, height: 30 }}
        >
          <Stack height={5}>
            <BronzeBar w={cardW} h={5} />
          </Stack>

          <YStack paddingHorizontal={34} paddingTop={34} paddingBottom={30}>
            <Text color={LOGIN.cardNavy} fontSize={22} fontWeight="700" lineHeight={27} marginBottom={4}>
              {l('login.title')}
            </Text>
            <Text color={LOGIN.subText} fontSize={13.5} marginBottom={24}>
              {l('login.subtitle')}
            </Text>

            {/* Credentials — only for in-app ROPC. PKCE ('pkce') redirects via the button below. */}
            {!usePkce ? (
              <>
            {/* identifier */}
            <Text color={LOGIN.labelText} fontSize={13} fontWeight="600" marginBottom={7}>
              {l('login.identifier')}
            </Text>
            <Input
              {...fieldProps}
              marginBottom={18}
              value={email}
              onChangeText={setEmail}
              placeholder="k.aliyev@asgroup.az"
              autoCapitalize="none"
              keyboardType="email-address"
              // Autofill: web password managers need the standard token + a real
              // <input> id; textContentType/importantForAutofill cover iOS/Android.
              id="username"
              autoComplete="username"
              textContentType="username"
              importantForAutofill="yes"
            />

            {/* password */}
            <Text color={LOGIN.labelText} fontSize={13} fontWeight="600" marginBottom={7}>
              {l('login.password')}
            </Text>
            <XStack position="relative" alignItems="center" marginBottom={22}>
              <Input
                {...fieldProps}
                flex={1}
                paddingRight={46}
                value={pw}
                onChangeText={setPw}
                placeholder="••••••••"
                secureTextEntry={!showPwd}
                autoCapitalize="none"
                // react-native-web wants the web token ('current-password'); RN
                // native wants 'password'. Both let managers fill the saved pair.
                id="current-password"
                autoComplete={Platform.OS === 'web' ? 'current-password' : 'password'}
                textContentType="password"
                importantForAutofill="yes"
                onSubmitEditing={submit}
              />
              <IconButton
                position="absolute"
                right={6}
                top={6}
                width={36}
                height={36}
                onPress={() => setShowPwd((v) => !v)}
              >
                <Icon name={showPwd ? 'eye-off' : 'eye'} size={19} color={LOGIN.iconMuted} />
              </IconButton>
            </XStack>
              </>
            ) : null}

            {/* Ошибка важнее подсказки: если браузерный флоу сорвался, показать надо его причину,
                а не «нажмите Задать пароль» ещё раз. При отказе ROPC текста ошибки нет — тогда
                видна подсказка. */}
            {error ? (
              <Text color={LOGIN.error} fontSize={13} marginBottom={14} textAlign="center">
                {error}
              </Text>
            ) : mustSetPassword ? (
              <Text color={LOGIN.labelText} fontSize={13} marginBottom={14} textAlign="center">
                {l('login.mustsetpassword')}
              </Text>
            ) : null}

            {/* submit */}
            <XStack
              height={48}
              borderRadius={11}
              backgroundColor={LOGIN.button}
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              animation="quick"
              shadowColor={LOGIN.button}
              shadowOpacity={0.5}
              shadowRadius={14}
              shadowOffset={{ width: 0, height: 10 }}
              hoverStyle={{ backgroundColor: LOGIN.buttonHover }}
              pressStyle={{ opacity: 0.94, y: 1 }}
              opacity={isAuthenticating ? 0.7 : 1}
              onPress={submit}
            >
              {isAuthenticating ? (
                <Spinner color="#FFFFFF" />
              ) : (
                <Text color="#FFFFFF" fontSize={15} fontWeight="700" letterSpacing={0.3}>
                  {l(mustSetPassword ? 'login.setpassword' : 'login.action')}
                </Text>
              )}
            </XStack>

            {/* «Забыли пароль?» — для учёток без AD (филиал Грузии, внешние). Доменному
                пользователю страница сразу говорит, что его пароль меняется в ИТ. */}
            <XStack justifyContent="center" marginTop={14}>
              <Text
                color={LOGIN.footerLink}
                fontSize={12.5}
                cursor="pointer"
                hoverStyle={{ opacity: 0.8 }}
                onPress={() => router.push('/reset-password')}
              >
                {l('pwreset.link')}
              </Text>
            </XStack>

            {/* SSO line */}
            <XStack alignItems="center" justifyContent="center" gap={8} marginTop={18}>
              <Icon name="lock" size={14} color={LOGIN.sso} />
              <Text color={LOGIN.sso} fontSize={12.5}>
                {l('login.ssoline')}
              </Text>
            </XStack>
          </YStack>
        </YStack>

        {/* footer */}
        <XStack justifyContent="center" alignItems="center" flexWrap="wrap" marginTop={22}>
          <Text color={LOGIN.footer} fontSize={12}>
            © 2026 AS Group · {l('login.support')}:{' '}
          </Text>
          <Text
            color={LOGIN.footerLink}
            fontSize={12}
            cursor="pointer"
            hoverStyle={{ opacity: 0.8 }}
            onPress={() => void Linking.openURL('mailto:helpdesk@asgroup.az')}
          >
            helpdesk@asgroup.az
          </Text>
        </XStack>
      </YStack>
    </YStack>
  )
}
